<template>
  <div
    class="MediaBankCardList w-full flex items-center gap-12 desk:gap-16 py-12 border-b border-border-subtle hover:bg-lighter transition-colors"
  >
    <!-- Thumbnail -->
    <div class="w-64 h-64 shrink-0 bg-lighter flex items-center justify-center overflow-hidden">
      <template v-if="asset.mediaType === 'Images'">
        <nuxt-img
          v-if="isNorceUrl && !imageError"
          :src="asset.thumbnailUrl"
          provider="norce"
          preset="plpimage"
          sizes="64px"
          loading="lazy"
          class="w-full h-full object-cover"
          :alt="asset.name"
          @error="imageError = true"
        />
        <img
          v-else-if="!isNorceUrl && !imageError"
          :src="asset.thumbnailUrl"
          loading="lazy"
          class="w-full h-full object-cover"
          :alt="asset.name"
          @error="imageError = true"
        >
        <img
          v-else
          src="/icons/no-image.svg"
          class="w-24 h-24 opacity-30"
          alt=""
        >
      </template>
      <template v-else>
        <img
          :src="documentIcon"
          class="w-32 h-32 opacity-40"
          alt=""
        >
      </template>
    </div>

    <!-- Info -->
    <div class="flex-1 min-w-0">
      <!-- Documents: show document name as title -->
      <template v-if="asset.mediaType === 'Documents'">
        <div class="type-headline-sm leading-tight line-clamp-1">
          {{ asset.name || asset.mediaSetTitle }}
        </div>
        <div class="type-xs text-text-subtle">
          {{ asset.mediaSetTitle }}
        </div>
      </template>
      <!-- Images: show product name and article number -->
      <template v-else>
        <div class="type-headline-sm leading-tight line-clamp-1">
          {{ asset.productName }}
        </div>
        <div class="type-xs text-text-subtle">
          {{ asset.articleNumber }} · {{ asset.mediaSetTitle }}
        </div>
      </template>
    </div>

    <!-- Actions -->
    <div class="flex items-center gap-8 shrink-0">
      <!-- Preview (images) / Open (documents) -->
      <button
        v-if="asset.mediaType === 'Images'"
        class="btn btn--secondary !h-32 !px-12 type-xs"
        @click.stop="$emit('preview', asset)"
      >
        {{ $t('mediaBank.card.preview') }}
      </button>
      <a
        v-else-if="isPdf"
        :href="asset.url"
        target="_blank"
        rel="noopener noreferrer"
        class="btn btn--secondary !h-32 !px-12 type-xs"
        @click.stop
      >
        {{ $t('mediaBank.card.open') }}
      </a>

      <!-- Download -->
      <button
        class="btn btn--secondary !h-32 !px-12 type-xs"
        @click.stop="$emit('download', asset)"
      >
        {{ $t('mediaBank.card.download') }}
      </button>

      <!-- Select/Favorite -->
      <button
        class="w-32 h-32 flex items-center justify-center border border-border-subtle bg-bg-base hover:bg-lighter transition-colors"
        :title="isFavorite ? $t('mediaBank.preview.removeSelected') : $t('mediaBank.preview.addSelected')"
        @click.stop="$emit('toggleFavorite', asset)"
      >
        <img
          :src="isFavorite ? '/icons/check.svg' : '/icons/plus.svg'"
          class="w-16 h-16"
          alt=""
        >
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useMediaBankStore } from '~/store/mediaBank';
import type { MediaAsset } from '#constants/types/mediaBank';

const { $t } = useNuxtApp();
const mediaBankStore = useMediaBankStore();

const props = defineProps<{
  asset: MediaAsset;
}>();

defineEmits<{
  (e: 'preview', asset: MediaAsset): void;
  (e: 'download', asset: MediaAsset): void;
  (e: 'toggleFavorite', asset: MediaAsset): void;
}>();

const imageError = ref(false);

const isNorceUrl = computed(() => props.asset.thumbnailUrl.includes('cdn-norce.tech'));
const isPdf = computed(() => props.asset.mimeType === 'application/pdf' || props.asset.fileType === '.pdf');
const isZip = computed(() => props.asset.mimeType.includes('zip') || props.asset.fileType === '.zip');
const documentIcon = computed(() => (isZip.value ? '/icons/file-zip.svg' : '/icons/file-pdf.svg'));
const isFavorite = computed(() => mediaBankStore.isFavorite(props.asset.id));
</script>
